import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableSortLabel,
  TableRow,
  TextField
} from '@mui/material';
import { fetchWithDemoFallback } from '../../../helpers/apiHelper';
import ASTITitle from '../../labels/ASTITitle';
import ASTIText from '../../labels/ASTIText';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

function formatMonthLabel(month) {
  const [year, monthNumber] = String(month || '').split('-');
  const parsedDate = new Date(Number(year), Number(monthNumber) - 1, 1);
  if (Number.isNaN(parsedDate.getTime())) {
    return month;
  }

  return parsedDate.toLocaleDateString('en-GB', {
    month: 'short',
    year: 'numeric'
  });
}

export default function BudgetPage() {
  const [months, setMonths] = useState([]);
  const [categories, setCategories] = useState([]);
  const [values, setValues] = useState({});
  const [avgSpendByCategory, setAvgSpendByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [recalculatingAverages, setRecalculatingAverages] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [savingCells, setSavingCells] = useState({});
  const [sortField, setSortField] = useState('avg6m');
  const [sortDirection, setSortDirection] = useState('desc');
  const [monthOffset, setMonthOffset] = useState(0);
  const [editingCategory, setEditingCategory] = useState(null);
  const [autoFillFuture, setAutoFillFuture] = useState(true);
  const monthsPerPage = 6;

  const fetchBudgetMatrix = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetchWithDemoFallback(`${API_BASE}/api/budgets`, {
        method: 'GET',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch budgets (HTTP ${response.status})`);
      }

      const data = await response.json();
      setMonths(Array.isArray(data?.months) ? data.months : []);
      setCategories(Array.isArray(data?.categories) ? data.categories : []);
      setValues(data?.values && typeof data.values === 'object' ? data.values : {});
      setAvgSpendByCategory(
        data?.avg_monthly_spend_last_6m && typeof data.avg_monthly_spend_last_6m === 'object'
          ? data.avg_monthly_spend_last_6m
          : {}
      );
    } catch (e) {
      setMonths([]);
      setCategories([]);
      setValues({});
      setAvgSpendByCategory({});
      setError(e?.message || 'Unable to load budget matrix.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBudgetMatrix();
  }, [fetchBudgetMatrix]);

  const handleRecalculateAverages = useCallback(async () => {
    setRecalculatingAverages(true);
    setError('');
    setStatus('');

    try {
      const response = await fetchWithDemoFallback(`${API_BASE}/api/budgets/recalculate-avg6m`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || `Failed to recalculate averages (HTTP ${response.status})`);
      }

      const result = await response.json();
      setStatus(
        `Updated 6M averages for ${Number(result?.updatedCategoryCount || 0)} categories (${Number(result?.updatedRowCount || 0)} budget rows).`
      );

      await fetchBudgetMatrix();
    } catch (e) {
      setError(e?.message || 'Unable to recalculate 6M averages.');
    } finally {
      setRecalculatingAverages(false);
    }
  }, [fetchBudgetMatrix]);

  const setCellValue = useCallback((category, month, nextValue) => {
    setValues((prev) => ({
      ...prev,
      [category]: {
        ...(prev[category] || {}),
        [month]: nextValue
      }
    }));
  }, []);

  const handleCellBlur = useCallback(async (category, month, rawValue) => {
    const normalizedText = String(rawValue ?? '').trim();

    if (normalizedText !== '' && !Number.isFinite(Number(normalizedText))) {
      setError(`Invalid number for ${category} in ${formatMonthLabel(month)}.`);
      return;
    }

    const amount = normalizedText === '' ? null : Number(normalizedText);
    const startIndex = months.indexOf(month);
    if (startIndex < 0) {
      return;
    }
    const targetMonths = autoFillFuture ? months.slice(startIndex) : [month];
    const targetCellKeys = targetMonths.map((targetMonth) => `${category}__${targetMonth}`);

    setSavingCells((prev) => {
      const next = { ...prev };
      targetCellKeys.forEach((key) => {
        next[key] = true;
      });
      return next;
    });

    setError('');
    setStatus('');

    try {
      const response = await fetchWithDemoFallback(`${API_BASE}/api/budgets`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entries: targetMonths.map((targetMonth) => ({
            category,
            budget_month: targetMonth,
            amount
          }))
        })
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || `Failed to save budget (HTTP ${response.status})`);
      }

      // Mirror the saved value to all subsequent months only after persistence succeeds.
      setValues((prev) => ({
        ...prev,
        [category]: {
          ...(prev[category] || {}),
          ...Object.fromEntries(targetMonths.map((targetMonth) => [targetMonth, amount === null ? '' : amount]))
        }
      }));

      const lastMonth = targetMonths[targetMonths.length - 1];
      setStatus(`Saved ${category} from ${formatMonthLabel(month)} to ${formatMonthLabel(lastMonth)}.`);
    } catch (e) {
      setError(e?.message || 'Unable to save budget value.');
    } finally {
      setSavingCells((prev) => {
        const next = { ...prev };
        targetCellKeys.forEach((key) => {
          delete next[key];
        });
        return next;
      });
    }
  }, [months, autoFillFuture]);

  const handleSort = useCallback((nextField) => {
    setSortField((prevField) => {
      if (prevField === nextField) {
        setSortDirection((prevDirection) => (prevDirection === 'asc' ? 'desc' : 'asc'));
        return prevField;
      }

      setSortDirection('asc');
      return nextField;
    });
  }, []);

  const getNumericValue = useCallback((category, month) => {
    const raw = values?.[category]?.[month];
    const num = Number(raw);
    return Number.isFinite(num) ? num : 0;
  }, [values]);

  const sortedCategories = useMemo(() => {
    const items = [...categories];

    items.sort((a, b) => {
      let result = 0;

      if (sortField === 'category') {
        result = String(a || '').localeCompare(String(b || ''));
      } else if (sortField === 'avg6m') {
        const avgA = Number(avgSpendByCategory?.[a] || 0);
        const avgB = Number(avgSpendByCategory?.[b] || 0);
        result = avgA - avgB;
      } else if (sortField.startsWith('month:')) {
        const month = sortField.slice('month:'.length);
        const valueA = getNumericValue(a, month);
        const valueB = getNumericValue(b, month);
        result = valueA - valueB;
      }

      if (result === 0) {
        result = String(a || '').localeCompare(String(b || ''));
      }

      return sortDirection === 'asc' ? result : -result;
    });

    return items;
  }, [avgSpendByCategory, categories, getNumericValue, sortDirection, sortField]);

  // Month pagination: show only 6 months at a time
  const visibleMonths = useMemo(() => {
    return months.slice(monthOffset, monthOffset + monthsPerPage);
  }, [months, monthOffset, monthsPerPage]);

  const canNavigateBack = monthOffset > 0;
  const canNavigateForward = monthOffset + monthsPerPage < months.length;

  const handlePrevMonths = useCallback(() => {
    setMonthOffset(Math.max(0, monthOffset - 1));
  }, [monthOffset]);

  const handleNextMonths = useCallback(() => {
    setMonthOffset(Math.min(Math.max(0, months.length - monthsPerPage), monthOffset + 1));
  }, [monthOffset, months.length, monthsPerPage]);

  return (
    <Box
      width="100vw"
      minHeight="100vh"
      padding="20px"
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      sx={{ backgroundColor: '#FFFFFF', color: '#2B1810', overflowY: 'hidden', overflowX: 'hidden', height: '100vh' }}
    >
      <Box sx={{ width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ flexShrink: 0, position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#FFFFFF', paddingBottom: 2 }}>
          <Box>
            <ASTITitle text="Budget" />
            <ASTIText text="Edit monthly budget amounts per category through December 2027." />
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Switch
                checked={autoFillFuture}
                onChange={(e) => setAutoFillFuture(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#C47A30'
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#C47A30'
                  }
                }}
              />
              <ASTIText text={autoFillFuture ? "Auto-fill future months" : "Edit single month only"} />
            </Box>
            <Button
              variant="contained"
              onClick={handleRecalculateAverages}
              sx={{ backgroundColor: '#5C7A30' }}
              disabled={loading || recalculatingAverages}
            >
              {recalculatingAverages ? 'Updating 6M Avg...' : 'Update 6M Avg'}
            </Button>
            <Button variant="contained" onClick={fetchBudgetMatrix} sx={{ backgroundColor: '#C47A30' }} disabled={loading || recalculatingAverages}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>

        {status && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {status}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box display="flex" alignItems="center" gap={2} sx={{ color: '#2B1810', mt: 3 }}>
            <CircularProgress size={20} />
            Loading budget matrix...
          </Box>
        )}

        {!loading && !error && categories.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No budget rows available yet. Save at least one category/month entry to populate the matrix.
          </Alert>
        )}

        {!loading && !error && categories.length > 0 && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', mt: 2, border: '1px solid #D4B896', borderRadius: '8px' }}>
            {/* Month Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderBottom: '1px solid #D4B896', backgroundColor: '#F9F5F0' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handlePrevMonths}
                disabled={!canNavigateBack}
                sx={{ color: '#2B1810', borderColor: '#D4B896' }}
              >
                ← Previous Month
              </Button>
              <Box sx={{ color: '#2B1810', fontWeight: 600 }}>
                {visibleMonths.length > 0 && `${formatMonthLabel(visibleMonths[0])} to ${formatMonthLabel(visibleMonths[visibleMonths.length - 1])}`}
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={handleNextMonths}
                disabled={!canNavigateForward}
                sx={{ color: '#2B1810', borderColor: '#D4B896' }}
              >
                Next Month →
              </Button>
            </Box>
            <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table size="small" stickyHeader sx={{ backgroundColor: '#FFFFFF', color: '#2B1810', minWidth: 1200 }}>
                <TableHead>
                  <TableRow sx={{ position: 'sticky', top: 0, zIndex: 7 }}>
                    <TableCell
                      title=""
                      sx={{
                        fontWeight: 'bold',
                        color: '#2B1810',
                        backgroundColor: '#F0E3D0',
                        borderColor: '#D4B896',
                        position: 'sticky',
                        left: 0,
                        zIndex: 8,
                        minWidth: 240
                      }}
                    >
                      <TableSortLabel
                        active={sortField === 'category'}
                        direction={sortField === 'category' ? sortDirection : 'asc'}
                        onClick={() => handleSort('category')}
                        sx={{ color: '#2B1810', '& .MuiTableSortLabel-icon': { color: '#2B1810 !important' } }}
                      >
                        Category
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      title=""
                      sx={{
                        fontWeight: 'bold',
                        color: '#2B1810',
                        backgroundColor: '#F0E3D0',
                        borderColor: '#D4B896',
                        minWidth: 150,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <TableSortLabel
                        active={sortField === 'avg6m'}
                        direction={sortField === 'avg6m' ? sortDirection : 'asc'}
                        onClick={() => handleSort('avg6m')}
                        sx={{ color: '#2B1810', '& .MuiTableSortLabel-icon': { color: '#2B1810 !important' } }}
                      >
                        Avg 6M Spend
                      </TableSortLabel>
                    </TableCell>
                    {visibleMonths.map((month) => (
                      <TableCell
                        key={month}
                        title=""
                        sx={{
                          fontWeight: 'bold',
                          color: '#2B1810',
                          backgroundColor: '#F0E3D0',
                          borderColor: '#D4B896',
                          minWidth: 120,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <TableSortLabel
                          active={sortField === `month:${month}`}
                          direction={sortField === `month:${month}` ? sortDirection : 'asc'}
                          onClick={() => handleSort(`month:${month}`)}
                          sx={{ color: '#2B1810', '& .MuiTableSortLabel-icon': { color: '#2B1810 !important' } }}
                        >
                          {formatMonthLabel(month)}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedCategories.map((category) => (
                    <TableRow key={category} sx={{ '& td': { borderColor: '#D4B896' }, '&:hover': { backgroundColor: '#F0E3D0' } }}>
                      <TableCell
                        title=""
                        sx={{
                          color: '#2B1810',
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          backgroundColor: '#FFFFFF',
                          minWidth: 240,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          {category}
                        </Box>
                        <Button
                          size="small"
                          variant={editingCategory === category ? 'contained' : 'outlined'}
                          onClick={() => setEditingCategory(editingCategory === category ? null : category)}
                          sx={{
                            minWidth: '60px',
                            backgroundColor: editingCategory === category ? '#5C7A30' : 'transparent',
                            color: editingCategory === category ? '#FFFFFF' : '#2B1810',
                            borderColor: '#D4B896',
                            fontSize: '11px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {editingCategory === category ? 'Done' : 'Edit'}
                        </Button>
                      </TableCell>
                      <TableCell title="" sx={{ color: '#2B1810', minWidth: 150, fontWeight: 600 }}>
                        {Number(avgSpendByCategory?.[category] || 0).toFixed(2)}
                      </TableCell>
                      {visibleMonths.map((month) => {
                        const cellKey = `${category}__${month}`;
                        const cellValue = values?.[category]?.[month] ?? '';

                        return (
                          <TableCell key={cellKey} title="" sx={{ color: '#2B1810', minWidth: 120 }}>
                            <TextField
                              title=""
                              type="number"
                              size="small"
                              value={cellValue}
                              onChange={(e) => setCellValue(category, month, e.target.value)}
                              onBlur={(e) => handleCellBlur(category, month, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.currentTarget.blur();
                                }
                              }}
                              disabled={editingCategory !== category || Boolean(savingCells[cellKey])}
                              inputProps={{ step: '0.01', title: '' }}
                              sx={{
                                width: 100,
                                '& .MuiInputBase-input': {
                                  color: '#2B1810',
                                  textAlign: 'right',
                                  fontSize: '12px'
                                },
                                '& input[type=number]': {
                                  MozAppearance: 'textfield'
                                },
                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0
                                },
                                '& .MuiOutlinedInput-root fieldset': { borderColor: '#D4B896' },
                                '& .MuiOutlinedInput-root:hover fieldset': { borderColor: '#C4A882' },
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#F4A7B9' }
                              }}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        </Box>
      </Box>
    </Box>
  );
}